import CryptoJS from 'crypto-js';
import { Identity } from './Identity';
import CognitoConfigStore from '../config/cognitoConfigStore';
import cleaningSessionStorage from '../../utility/cleaningSessionStorage';
import { jwtDecode } from 'jwt-decode';

const storage = sessionStorage;
const baseKey = 'atcog-ksgx1a';
const verifierKey = baseKey + '-verifier';
const stateKey = baseKey + '-pkcestate';
const accessTokenKey = baseKey + '-accesstoken';
const idTokenKey = baseKey + '-idtoken';
const refreshTokenKey = baseKey + '-refreshtoken';

export class AwsCognitoIdentity extends Identity {
  _expireLimit: any;
  _refreshTimer: any;
  cfg: CognitoConfigStore;

  constructor(cfg: CognitoConfigStore) {
    super();
    this._expireLimit = 180000;
    this._refreshTimer = null;
    this.generateVerifier();
    this.generateState();
    this.cfg = cfg;
  }

  isAuthenticated() {
    let code = this.getUrlParameter('code');
    if (code) {
      let state = this.getUrlParameter('state');
      if (state !== this.state()) {
        console.log('Invalid state');
        return Promise.resolve(false);
      } else {
        return this.acquireTokens(code)
          .then(() => {
            window.history.replaceState({}, document.title, '/');
            return true;
          })
          .catch(() => {
            return false;
          });
      }
    }

    return this.tokens()
      .then((tokens) => {
        if (tokens) {
          this.startTokenRefreshTimer(tokens.accessToken);
          return true;
        }
        return false;
      })
      .catch((err) => {
        console.error(err);
        return false;
      });
  }

  signIn() {
    return Promise.resolve(window.location.assign(this.signInUrl()));
  }

  signOut() {
    cleaningSessionStorage();
    return Promise.resolve(window.location.assign(this.signOutUrl()));
  }

  forgotPassword() {
    return Promise.resolve(window.location.assign(this.forgotPwdUrl()));
  }

  hasForgotPassword() {
    return !!this.cfg.getForgotPwdUrl();
  }

  mfaSetup() {
    return Promise.resolve(window.location.assign(this.cfg.getMfaSetupUrl()));
  }

  hasMfaSetup() {
    return !!this.cfg.getMfaSetupUrl();
  }

  tokens() {
    let access = storage.getItem(accessTokenKey);
    if (!access) return Promise.resolve(null);

    if (this.tokenExpiresIn(access) <= this._expireLimit) return this.refreshTokens();

    return Promise.resolve({
      accessToken: access,
      idToken: storage.getItem(idTokenKey),
    });
  }

  accessToken() {
    return Promise.resolve(storage.getItem(accessTokenKey));
  }

  idToken() {
    return Promise.resolve(storage.getItem(idTokenKey));
  }

  displayName() {
    return this.idToken().then((token) => {
      if (token) {
        let decoded: any = jwtDecode(token);
        return decoded.name || decoded.username || decoded['cognito:username'];
      }
      return '';
    });
  }

  acquireTokens(code: any) {
    return fetch(this.cfg.getTokenUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body:
        'grant_type=authorization_code&client_id=' +
        this.cfg.getClientId() +
        '&code_verifier=' +
        this.verifier() +
        '&redirect_uri=' +
        this.cfg.getRedirectUri() +
        '&code=' +
        code,
    })
      .then((response) => {
        return response.json().then((data) => {
          this.setTokens(data);
          storage.setItem(refreshTokenKey, data.refresh_token);
          return {
            accessToken: data.access_token,
            idToken: data.id_token,
          };
        });
      })
      .catch((err) => {
        console.error('error acquiring cognito tokens', err);
      });
  }

  refreshTokens() {
    let refresh = storage.getItem(refreshTokenKey);
    if (!refresh) return Promise.resolve(null);

    return fetch(this.cfg.getTokenUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: 'grant_type=refresh_token&client_id=' + this.cfg.getClientId() + '&refresh_token=' + refresh,
    })
      .then((response) => {
        return response.json().then((data) => {
          this.setTokens(data);
          console.log('tokens refreshed');
          return {
            accessToken: data.access_token,
            idToken: data.id_token,
          };
        });
      })
      .catch((err) => {
        console.error('error refreshing cognito tokens', err);
      });
  }

  setTokens(data: any) {
    storage.setItem(accessTokenKey, data.access_token);
    storage.setItem(idTokenKey, data.id_token);
    this.startTokenRefreshTimer(data.access_token);
  }

  startTokenRefreshTimer(token: string) {
    if (this._refreshTimer) clearTimeout(this._refreshTimer);

    let timer = this.tokenExpiresIn(token) - this._expireLimit;
    this._refreshTimer = setTimeout(() => {
      this.refreshTokens();
    }, timer);
  }

  tokenExpiresIn(token: string) {
    let decoded: any = jwtDecode(token);
    let exp = new Date(decoded.exp * 1000);
    return exp.getTime() - Date.now();
  }

  signInUrl() {
    return (
      this.cfg.getBaseUrl() +
      '/oauth2/authorize?scope=' +
      this.cfg.getScope() +
      '&response_type=code&client_id=' +
      this.cfg.getClientId() +
      '&code_challenge=' +
      this.challenge() +
      '&code_challenge_method=S256&redirect_uri=' +
      this.cfg.getRedirectUri() +
      '&state=' +
      this.state()
    );
  }

  signOutUrl() {
    return this.cfg.getBaseUrl() + '/logout?client_id=' + this.cfg.getClientId() + '&logout_uri=' + this.cfg.getRedirectUri();
  }

  forgotPwdUrl() {
    return `${this.cfg.getForgotPwdUrl()}?scope=${this.cfg.getScope()}&response_type=code&client_id=${this.cfg.getClientId()}` +
      `&code_challenge=${this.challenge()}&code_challenge_method=S256&redirect_uri=${this.cfg.getRedirectUri()}&state=${this.state()}`;
  }

  challenge() {

    return this.toBase64Url(CryptoJS.SHA256(this.verifier()));
  }

  verifier() {
    return storage.getItem(verifierKey);
  }

  state() {
    return storage.getItem(stateKey);
  }

  generateVerifier() {
    let verifier: string | null = storage.getItem(verifierKey);
    if (!verifier) {
      verifier = this.toBase64Url(CryptoJS.lib.WordArray.random(32));
      storage.setItem(verifierKey, verifier);
    }
  }

  generateState() {
    let state: string | null = storage.getItem(stateKey);
    if (!state) {
      state = this.toBase64Url(CryptoJS.lib.WordArray.random(28));
      storage.setItem(stateKey, state);
    }
  }

  toBase64Url(hash: any) {
    return hash.toString(CryptoJS.enc.Base64).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  }
}
